import { useDrawer } from '@iveco-ti/iveco-ds'
import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Newspaper, ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'
import { NewsContentForm } from '../../schemas/NewsContentSchema'
import { NewsCropForm } from '../../schemas/NewsImageSchema'
import { NewsOrderForm } from '../../schemas/NewsOrderSchema'
import { NewsSummaryForm } from '../../schemas/NewsSummarySchema'

async function postNews(data: FormData) {
  return api.backend.post('/posts', data)
}

type Form = NewsSummaryForm & NewsContentForm & NewsCropForm & NewsOrderForm

export function useCreateNews() {
  const { t } = useTranslation('panel')
  const drawerCreateNews = useDrawer('drawer-create-news')
  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    FormData
  >({
    mutationFn: postNews,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: Newspaper,
          title: t('news.use_create_news.toast_success.title'),
        },
      })
      drawerCreateNews.close()
      queryClient.invalidateQueries({ queryKey: ['/posts/paginate'] })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('news.use_create_news.toast_error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('news.use_create_news.toast_error.description'),
        },
      })
    },
  })

  const handleCreateNews = async (form: Form) => {
    const data = new FormData()

    if (form.postImage) data.append('postImage', form.postImage)
    if (form.postFile) data.append('postFile', form.postFile)

    const body = {
      isSlider: false,
      title: form.title,
      subTitle: form.subtitle,
      post: {
        content: form.content,
      },
      postExternalUrl: form.postExternalUrl,
      tag: form.tag?.map((tag) => tag.value) || [],
    }

    data.append('body', JSON.stringify(body))

    await mutateAsync(data)
  }

  return {
    handleCreateNews,
    isCreatingNews: isPending,
  }
}
